import { getHolidays, getSchoolDays } from '@bp/shared-planung-utils';
import { Grid, GridColumn, GridRow, Input, ModalBottomButtons } from '@bp/ui-components';
import dayjs from 'dayjs';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  useSchoolYearsQuery,
  useSetTimetableBlockTeachingLoadFactorMutation,
} from '../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessUpdateToast } from '../../../utils/toast';
import { TIMETABLE_BLOCK_MUTATION_CONTEXT, useTimetableBlocks } from '../hooks/useTimetableBlocks';

type TimetableBlockFactorFormType = {
  timetableBlockUuid: string;
  comment: string | null;
  schoolDaysPercent: number;
  factor: number;
};

type TimetableBlockFactorFormProps = {
  timetableBlockUuid: string;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

export const TimetableBlockFactorForm = observer(
  ({ timetableBlockUuid, onLoad, onClose }: TimetableBlockFactorFormProps) => {
    const { t } = useTranslation();
    const schoolYear = useSelectedSchoolYear();
    const schoolYearContext = useMemorizedCacheTag('SCHOOL_YEAR');

    const [, setFactor] = useSetTimetableBlockTeachingLoadFactorMutation();

    const { timetableBlocks } = useTimetableBlocks(schoolYear?.uuid, { onlyWithActiveVersion: true });

    const [{ data: schoolYearsData }] = useSchoolYearsQuery({
      context: schoolYearContext,
    });

    const timetableBlock = timetableBlocks?.find((b) => b.uuid === timetableBlockUuid);
    if (!timetableBlock) return;

    const holidays = schoolYearsData?.schoolYears.find((s) => s.uuid === schoolYear?.uuid)?.holidays ?? [];
    const holidaysSet = getHolidays(
      null,
      holidays.map((h) => ({ start: dayjs(h.start), end: dayjs(h.end) })),
    );

    const daysTotal =
      timetableBlocks?.reduce((prev, curr) => {
        const dayCount = getSchoolDays(dayjs(curr.start), dayjs(curr.end), holidaysSet).size;
        return prev + dayCount;
      }, 0) ?? 1;

    const days = getSchoolDays(dayjs(timetableBlock.start), dayjs(timetableBlock.end), holidaysSet).size;

    const initialValues: TimetableBlockFactorFormType = {
      timetableBlockUuid: timetableBlockUuid,
      comment: timetableBlock.teachingLoadComment ?? null,
      schoolDaysPercent: Math.round((100 / daysTotal) * days * 100) / 100,
      factor: timetableBlock.teachingLoadFactor ?? 1,
    };

    async function handleSubmit(
      values: TimetableBlockFactorFormType,
      { resetForm }: FormikHelpers<TimetableBlockFactorFormType>,
    ) {
      onLoad(true);

      const { error } = await setFactor(
        { uuid: timetableBlockUuid, factor: values.factor, comment: values.comment },
        TIMETABLE_BLOCK_MUTATION_CONTEXT,
      );

      if (error) {
        showErrorToast(error);
      } else {
        showSuccessUpdateToast();
      }

      onLoad(false);
      resetForm();
      onClose();
    }

    return (
      <Formik<TimetableBlockFactorFormType> initialValues={initialValues} onSubmit={handleSubmit}>
        {({ values, resetForm, submitForm, isSubmitting, setFieldValue, dirty }) => {
          return (
            <Form>
              <Grid useFormGap>
                <GridRow spacingBottom='none'>
                  <Input
                    name='comment'
                    label={t('common.comment')}
                    value={values.comment}
                    onChange={async (e) => await setFieldValue('comment', e.target.value)}
                  />
                </GridRow>
                <GridRow spacingTop='none'>
                  <GridColumn width={6}>
                    <Input
                      name='school-days-percent'
                      label={t('timetableBlock.schoolDays')}
                      value={values.schoolDaysPercent}
                      suffix='%'
                      readonly
                    />
                  </GridColumn>
                  <GridColumn width={6}>
                    <Input
                      name='factor'
                      label={t('common.factor')}
                      value={Math.round(values.factor * 10000) / 100}
                      onChange={async (e) => await setFieldValue('factor', Number(e.target.value) / 100)}
                      suffix='%'
                      type='number'
                      min={0}
                      max={100}
                      autoFocus
                    />
                  </GridColumn>
                </GridRow>
              </Grid>

              <ModalBottomButtons
                closeButton={{
                  callback: () => {
                    onClose();
                    resetForm();
                  },
                }}
                submitButton={{
                  disabled: isSubmitting || !dirty,
                  callback: async () => {
                    await submitForm();
                  },
                }}
              />
            </Form>
          );
        }}
      </Formik>
    );
  },
);
